// UrVenueOverrides — thin wrapper around PlatformOverridesShell for
// UrVenue per-event overrides. All generic UI (the resolved-value form with
// per-field widgets, inheritance badges, drift compare with per-field
// push/accept, full payload toggle, breadcrumb) comes from the shell — only
// the UrVenue field catalog, inheritance lookup, and source-key aliases are
// platform-specific.

import PlatformOverridesShell from './platforms/PlatformOverridesShell.jsx';

// Catalog keys whose resolver `sources` entry uses a different path. Keeps the
// inherited-source badge accurate; override detection is by catalog key.
const URVENUE_SOURCE_KEY = {
  event_type_id: 'eventtypeid',
  inventory_template_id: 'inventory.template_id',
  place_id: 'inventory.place_id',
  ecozone: 'inventory.ecozone',
  open_24h: 'schedule.open_24h',
  shifts: 'schedule.shifts',
};

// Curated allowlist of UrVenue fields ops can override per-event.
// Everything not here is venue/template-level only.
const URVENUE_OVERRIDE_FIELDS = [
  // Display
  { key: 'name',                  group: 'Display',     label: 'Event name',                 kind: 'text' },
  { key: 'publicdescr',           group: 'Display',     label: 'Public description',         kind: 'html' },
  { key: 'privatedescr',          group: 'Display',     label: 'Private description',        kind: 'html' },
  { key: 'shortdescr',            group: 'Display',     label: 'Short description',          kind: 'text' },
  { key: 'promovideourl',         group: 'Display',     label: 'Promo video URL',            kind: 'text' },
  { key: 'tags',                  group: 'Display',     label: 'Tags',                       kind: 'text' },
  // Event type + times
  { key: 'event_type_id',         group: 'Event type',  label: 'Event type id (Nightlife/Private/etc)', kind: 'text' },
  { key: 'performers',            group: 'Performers', label: 'Performers',                    kind: 'custom' },
  { key: 'starttime',             group: 'Times',       label: 'Start time',                 kind: 'time' },
  { key: 'endtime',               group: 'Times',       label: 'End time',                   kind: 'time' },
  { key: 'doorsopen',             group: 'Times',       label: 'Doors open',                 kind: 'time' },
  // Inventory (per-event override of template-level VIP table layout)
  { key: 'inventory_template_id', group: 'Inventory',   label: 'Inventory template id',      kind: 'text' },
  { key: 'place_id',              group: 'Inventory',   label: 'Place id (room)',            kind: 'number' },
  { key: 'ecozone',               group: 'Inventory',   label: 'Ecozone id',                 kind: 'number' },
  { key: 'marketareaid',          group: 'Inventory',   label: 'Market area id',             kind: 'text' },
  // Schedule
  { key: 'open_24h',              group: 'Schedule',    label: 'Open 24h (override DOW closure)', kind: 'bool' },
  { key: 'shifts',                group: 'Schedule',    label: 'Shift schedule override',    kind: 'text' },
  // Flags
  { key: 'priority',              group: 'Flags',       label: 'Priority',                   kind: 'number' },
  { key: 'private',               group: 'Flags',       label: 'Private flag',               kind: 'number' },
  { key: 'inquiries',             group: 'Flags',       label: 'Inquiries flag',             kind: 'number' },
  { key: 'ismarketevent',         group: 'Flags',       label: 'Market event flag',          kind: 'number' },
  { key: 'releasedate',           group: 'Flags',       label: 'Release date',               kind: 'date' },
  // Images (URL override; flyer upload still flows through publisher)
  { key: 'flyer_url',             group: 'Images',      label: 'Custom flyer URL (overrides template default)', kind: 'text' },
];

// UrVenue resolver returns a structured payload — normalize lookup so the
// picker can preview inherited values for any catalog key.
function urvenueInheritedValue(resolved, key) {
  if (!resolved) return undefined;
  const p = resolved.payload || {};
  if (key === 'inventory_template_id') return p.inventory?.template_id;
  if (key === 'place_id')              return p.inventory?.place_id;
  if (key === 'ecozone')               return p.inventory?.ecozone;
  if (key === 'open_24h')              return p.schedule?.open_24h;
  if (key === 'shifts')                return p.schedule?.shifts;
  if (key === 'marketareaid')          return p.marketareaid;
  if (key === 'flyer_url')             return p.flyer_url;
  if (key === 'event_type_id')         return p.eventdata?.eventtypeid;
  // Clock strings (HH:MM) for the time picker — eventdata only has raw ints.
  if (key === 'starttime')             return p.times?.starttime;
  if (key === 'endtime')               return p.times?.endtime;
  if (key === 'doorsopen')             return p.times?.doorsopen;
  // Everything else maps straight to eventdata
  return p.eventdata?.[key];
}

export default function UrVenueOverrides() {
  return (
    <PlatformOverridesShell
      platform="urvenue"
      platformLabel="UrVenue"
      overridesKey="urvenue_overrides"
      fieldCatalog={URVENUE_OVERRIDE_FIELDS}
      inheritedValue={urvenueInheritedValue}
      sourceKey={(k) => URVENUE_SOURCE_KEY[k] || k}
      pushOnSave
      overviewTab
      promoteFields
      promotable={{
        venue: ['event_type_id', 'starttime', 'endtime', 'marketareaid'],
        template: ['event_type_id', 'starttime', 'endtime', 'doorsopen', 'inventory_template_id', 'place_id', 'ecozone', 'open_24h', 'shifts', 'marketareaid'],
      }}
    />
  );
}
